import { useState, useEffect } from "react";
import { Table, Button, Space, Tag, Modal, Input } from "antd";
import { useHistory } from "react-router-dom";
import dayjs from "dayjs";
import jsPDF from "jspdf";

import { getAllBlueprints, updateBlueprint } from "../apis/erpSyncBlueprint";
import { useRoleContext } from "../contexts/Role";
import { blueprintKeyTransform, allFields } from "../utils/blueprint";
import notoSansTCBase64 from "../utils/base64Encode";

const { TextArea } = Input;

const SpecHistory = () => {
  const history = useHistory();

  const { appRole } = useRoleContext();

  const [blueprintData, setBlueprintData] = useState();
  const [createdByArr, setCreatedByArr] = useState([]);
  const [editMemoUuid, setEditMemoUuid] = useState(null);
  const [memoValue, setMemoValue] = useState("");

  function fetchBlueprintData() {
    getAllBlueprints()
      .then((data) => {
        const descData = data["data"].map(item => Object.assign(item, { key: item["uuid"] })).sort((prev, curr) =>
          dayjs(prev["createTime"]).isBefore(dayjs(curr["createTime"]))
        );
        const uniqueCreators = [
          ...new Set(descData.map((item) => item["createdBy"])),
        ];

        setBlueprintData(descData);
        setCreatedByArr(uniqueCreators);
      })
      .catch((error) =>
        console.error("Error while getAllBlueprints - SpecHistory", error)
      );
  }

  const onOkMemoModal = (uuid) => {
    updateBlueprint(uuid, {
      memo: memoValue,
    })
      .then(() => {
        setEditMemoUuid(null);
        fetchBlueprintData();
      })
      .catch((error) => {
        console.error("Error while updateBlueprintMemo - SpecHistory", error);
      });
  };

  const exportPDF = (record) => {
    const doc = new jsPDF({
      unit: "pt",
      format: "a4",
    });

    /* for solving chinese word problem */
    doc.addFileToVFS("NotoSansTC-Regular.ttf", notoSansTCBase64); // base64 font data
    doc.addFont("NotoSansTC-Regular.ttf", "NotoSansTC", "normal");
    doc.setFont("NotoSansTC");
    doc.setFontSize(12);

    const marginX = 40;
    const marginY = 40;
    const lineHeight = 20;
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const usableWidth = pageWidth - marginX * 2;

    let currentY = marginY;

    doc.text(`ERP Sync Blueprint`, marginX, currentY);
    currentY += lineHeight;
    doc.text(`客戶: ${record["tenantId"]}`, marginX, currentY);
    currentY += lineHeight;
    doc.text(
      `創建時間: ${dayjs(record["createTime"]).format("YYYY-MM-DD HH:mm:ss")}`,
      marginX,
      currentY
    );
    currentY += lineHeight;
    doc.text(`創建人: ${record["createdBy"]}`, marginX, currentY);
    currentY += lineHeight;
    currentY += lineHeight;

    function addTextLine(text) {
      //// Check for page break
      if (currentY + lineHeight > pageHeight - marginY) {
        doc.addPage();
        currentY = marginY;
      }
      doc.text(text, marginX, currentY);
      currentY += lineHeight;
    }

    //// Add possibly multi-line text (handles \n and long lines)
    function addWrappedText(text, indent = 0) {
      const indentSpace = " ".repeat(indent * 4);
      const lines = text.split("\n");

      for (const line of lines) {
        //// Split text that exceeds usable width
        const wrapped = doc.splitTextToSize(indentSpace + line, usableWidth);
        for (const sub of wrapped) {
          addTextLine(sub);
        }
      }
    }

    const order1 = [
      "connectionConfig",
      "fieldMapping",
      "syncRule",
      "updateRule",
      "blueprintMemo",
    ];
    const order2 = ["required", "optional"];
    const order3 = [
      "work-order",
      "product",
      "work-order-op-history",
      "route",
      "operation",
      "equipment",
      "worker",
    ];
    function arrangePdfData(data, indent = 0) {
      let sourceAry = data[order1[0]]
        ? order1
        : data[order2[0]]
          ? order2
          : data[order3[0]]
            ? order3
            : Object.keys(data);
      for (const key of sourceAry) {
        const value = data[key];
        if (value !== undefined) {
          if (
            (typeof value === "object" && !Array.isArray(value)) ||
            (Array.isArray(value) &&
              value.length > 0 &&
              typeof value[0] === "object")
          ) {
            addWrappedText(
              `${blueprintKeyTransform[key] || allFields[key]?.["label"] || key
              }:`,
              indent
            );
            arrangePdfData(value, indent + 1);
          } else {
            addWrappedText(
              `${blueprintKeyTransform[key] || allFields[key]?.["label"] || key
              }: ${value}`,
              indent
            );
          }
        }

        if (order1.includes(key)) {
          currentY += lineHeight;
        }
      }
    }

    arrangePdfData(record["blueprint"]);

    doc.save("data-export.pdf");
  };

  const tableCols = [
    {
      key: "createTime",
      dataIndex: "createTime",
      title: "創建時間",
      width: "20%",
      sorter: (prev, curr) => (dayjs(prev).isBefore(dayjs(curr)) ? 1 : -1),
      render: (value) => dayjs(value).format("YYYY-MM-DD HH:mm:ss"),
    },
    {
      key: "createdBy",
      dataIndex: "createdBy",
      title: "創建人員",
      width: "20%",
      filters: createdByArr.map((creator) => {
        return { text: creator, value: creator };
      }),
      filterSearch: true,
      onFilter: (value, record) => record["createdBy"] === value,
    },
    {
      key: "status",
      dataIndex: "status",
      title: "狀態",
      width: "15%",
      filters: [
        { text: "待審核...", value: 0 },
        { text: "審核不通過", value: 1 },
        { text: "審核通過", value: 2 },
      ],
      onFilter: (value, record) => record["status"] === value,
      render: (value) => {
        return value === 0 ? (
          <Tag color="gold">待審核...</Tag>
        ) : value === 1 ? (
          <Tag color="blue">審核不通過</Tag>
        ) : value === 2 ? (
          <Tag color="green">審核通過</Tag>
        ) : null;
      },
    },
    appRole !== "onlyView"
      ? {
        key: "memo",
        title: "備註",
        width: "15%",
        dataIndex: "memo",
        render: (value) => (
          <div style={{ whiteSpace: "pre-wrap" }}>{value}</div>
        ),
      }
      : {},
    {
      key: "actions",
      title: "操作",
      render: (record) => {
        return (
          <Space>
            {appRole === "allowApprove" && record["status"] === 0 ? (
              <Button
                danger
                type="primary"
                onClick={() =>
                  history.push(`/setting/${record["uuid"]}`, {
                    stateBlueprint: {
                      [record["uuid"]]: record,
                      approval: true,
                    },
                  })
                }
              >
                進行審核
              </Button>
            ) : null}
            {appRole !== "onlyView" ? (
              <>
                <Button
                  type="primary"
                  onClick={() =>
                    history.push(`/setting/${record["uuid"]}`, {
                      stateBlueprint: { [record["uuid"]]: record },
                    })
                  }
                >
                  以此紀錄為模板更新配置
                </Button>
                <Button
                  onClick={() => {
                    setEditMemoUuid(record["uuid"]);
                    setMemoValue(record["memo"]);
                  }}
                >
                  編輯備註
                </Button>
              </>
            ) : null}
            <Button onClick={() => exportPDF(record)}>匯出 PDF</Button>
          </Space>
        );
      },
    },
  ];

  useEffect(() => {
    fetchBlueprintData();
  }, []);

  return (
    <div>
      <Table
        loading={!blueprintData}
        columns={tableCols}
        dataSource={blueprintData}
      />
      <Modal
        width="50%"
        title="備註"
        open={editMemoUuid ? true : false}
        onCancel={() => setEditMemoUuid(null)}
        onOk={() => onOkMemoModal(editMemoUuid)}
      >
        <TextArea
          rows={10}
          value={memoValue}
          onChange={(e) => setMemoValue(e.target.value)}
        />
      </Modal>
    </div>
  );
};

export default SpecHistory;
